import React, { useState, useEffect } from 'react';
    import { motion } from 'framer-motion';
    import { supabase } from '../../config/supabase';
    import SafeIcon from '../../common/SafeIcon';
    import CSVUploader from './CSVUploader';
    import ProfileEditModal from './ProfileEditModal';
    import * as FiIcons from 'react-icons/fi';

    const {
      FiEdit2,
      FiTrash2,
      FiDownload,
      FiUpload,
      FiSearch,
      FiStar,
      FiRefreshCw,
    } = FiIcons;

    const ProfilesTab = () => {
      const [profiles, setProfiles] = useState([]);
      const [filteredProfiles, setFilteredProfiles] = useState([]);
      const [selectedProfiles, setSelectedProfiles] = useState([]);
      const [searchQuery, setSearchQuery] = useState('');
      const [currentPage, setCurrentPage] = useState(1);
      const [loading, setLoading] = useState(true);
      const [editingProfile, setEditingProfile] = useState(null);
      const [showCSVUploader, setShowCSVUploader] = useState(false);
      const itemsPerPage = 20;

      useEffect(() => {
        fetchProfiles();
      }, []);

      useEffect(() => {
        filterProfiles();
      }, [profiles, searchQuery]);

      const fetchProfiles = async () => {
        setLoading(true);
        try {
          let allProfiles = [];
          let rangeStart = 0;
          const rangeSize = 1000;
          let hasMore = true;

          while (hasMore) {
            const { data: profilesBatch, error } = await supabase
              .from('profiles')
              .select('*')
              .eq('status', 'approved')
              .order('created_at', { ascending: false })
              .range(rangeStart, rangeStart + rangeSize - 1);

            if (error) throw error;

            if (profilesBatch && profilesBatch.length > 0) {
              allProfiles = [...allProfiles, ...profilesBatch];
              rangeStart += rangeSize;
            } else {
              hasMore = false;
            }

            if (!profilesBatch || profilesBatch.length < rangeSize) {
              hasMore = false;
            }
          }
          setProfiles(allProfiles);
        } catch (error) {
          console.error('Error fetching profiles:', error);
        } finally {
          setLoading(false);
        }
      };

      const filterProfiles = () => {
        if (!searchQuery) {
          setFilteredProfiles(profiles);
        } else {
          const filtered = profiles.filter(
            (profile) =>
              profile.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              profile.instagram_handle.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (profile.tags &&
                profile.tags.some((tag) =>
                  tag.toLowerCase().includes(searchQuery.toLowerCase())
                ))
          );
          setFilteredProfiles(filtered);
        }
        setCurrentPage(1);
      };

      const handleToggleHighlight = async (profile) => {
        try {
          const { error } = await supabase
            .from('profiles')
            .update({ is_highlighted: !profile.is_highlighted })
            .eq('id', profile.id);
          if (error) throw error;
          fetchProfiles();
        } catch (error) {
          console.error('Error toggling highlight status:', error);
        }
      };

      const handleDelete = async (profileId) => {
        if (window.confirm('Are you sure you want to delete this profile?')) {
          try {
            const { error } = await supabase
              .from('profiles')
              .delete()
              .eq('id', profileId);
            if (error) throw error;
            fetchProfiles();
          } catch (error) {
            console.error('Error deleting profile:', error);
          }
        }
      };

      const handleBulkDelete = async () => {
        if (selectedProfiles.length === 0) return;
        if (
          window.confirm(
            `Are you sure you want to delete ${selectedProfiles.length} profiles?`
          )
        ) {
          try {
            const { error } = await supabase
              .from('profiles')
              .delete()
              .in('id', selectedProfiles);
            if (error) throw error;
            setSelectedProfiles([]);
            fetchProfiles();
          } catch (error) {
            console.error('Error deleting profiles:', error);
          }
        }
      };

      const handleExport = () => {
        const csvContent = [
          'Instagram Handle',
          ...filteredProfiles.map((profile) => profile.instagram_handle),
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'instagram-usernames.csv';
        a.click();
        URL.revokeObjectURL(url);
      };

      const toggleProfileSelection = (profileId) => {
        setSelectedProfiles((prev) =>
          prev.includes(profileId)
            ? prev.filter((id) => id !== profileId)
            : [...prev, profileId]
        );
      };

      const toggleSelectAll = () => {
        const currentPageProfiles = getCurrentPageProfiles();
        const allSelected = currentPageProfiles.every((profile) =>
          selectedProfiles.includes(profile.id)
        );
        if (allSelected) {
          setSelectedProfiles((prev) =>
            prev.filter((id) => !currentPageProfiles.find((p) => p.id === id))
          );
        } else {
          setSelectedProfiles((prev) => [
            ...prev,
            ...currentPageProfiles
              .filter((profile) => !prev.includes(profile.id))
              .map((profile) => profile.id),
          ]);
        }
      };

      const getCurrentPageProfiles = () => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredProfiles.slice(startIndex, startIndex + itemsPerPage);
      };

      if (loading && profiles.length === 0) {
        return (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        );
      }

      const totalProfiles = filteredProfiles.length;
      const totalPages = Math.ceil(totalProfiles / itemsPerPage);
      const currentPageProfiles = getCurrentPageProfiles();

      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {' '}
              Approved Profiles ({totalProfiles}){' '}
            </h2>
          </div>

          {/* Header Actions */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-2 flex-wrap gap-2">
              <button
                onClick={() => setShowCSVUploader(true)}
                className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                <SafeIcon icon={FiUpload} />
                <span>Import CSV</span>
              </button>
              <button
                onClick={handleExport}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <SafeIcon icon={FiDownload} />
                <span>Export Usernames</span>
              </button>
              <button
                onClick={fetchProfiles}
                disabled={loading}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
                <span>{loading ? 'Syncing...' : 'Sync Profiles'}</span>
              </button>
              {selectedProfiles.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  <SafeIcon icon={FiTrash2} />
                  <span>Delete ({selectedProfiles.length})</span>
                </button>
              )}
            </div>
            <div className="relative">
              <SafeIcon
                icon={FiSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or handle..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          {/* Profiles Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={
                          currentPageProfiles.length > 0 &&
                          currentPageProfiles.every((profile) =>
                            selectedProfiles.includes(profile.id)
                          )
                        }
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Handle</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Tags</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Highlight</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentPageProfiles.map((profile) => (
                    <motion.tr
                      key={profile.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedProfiles.includes(profile.id)}
                          onChange={() => toggleProfileSelection(profile.id)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{profile.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">@{profile.instagram_handle}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(profile.tags || []).slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full"
                            >
                              {' '}
                              {tag}{' '}
                            </span>
                          ))}
                          {(profile.tags || []).length > 3 && (
                            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                              {' '}
                              +{(profile.tags || []).length - 3}{' '}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleToggleHighlight(profile)}
                          className={`p-2 rounded-md transition-colors ${
                            profile.is_highlighted
                              ? 'text-yellow-500 hover:bg-yellow-50'
                              : 'text-gray-400 hover:bg-gray-100'
                          }`}
                          title={
                            profile.is_highlighted
                              ? 'Remove Highlight'
                              : 'Highlight Profile'
                          }
                        >
                          <SafeIcon
                            icon={FiStar}
                            className={profile.is_highlighted ? 'fill-current' : ''}
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex space-x-1">
                          <button
                            onClick={() => setEditingProfile(profile)}
                            className="p-2 text-purple-600 hover:text-purple-700 rounded-md hover:bg-purple-50 transition-colors"
                          >
                            <SafeIcon icon={FiEdit2} />
                          </button>
                          <button
                            onClick={() => handleDelete(profile.id)}
                            className="p-2 text-red-600 hover:text-red-700 rounded-md hover:bg-red-50 transition-colors"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  {' '}
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, totalProfiles)} of {totalProfiles} results{' '}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    {' '}
                    Previous{' '}
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 border rounded text-sm ${
                        page === currentPage
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {' '}
                      {page}{' '}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    {' '}
                    Next{' '}
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Modals */}
          {showCSVUploader && <CSVUploader onClose={() => setShowCSVUploader(false)} onSuccess={fetchProfiles} />}
          {editingProfile && (
            <ProfileEditModal
              profile={editingProfile}
              onClose={() => setEditingProfile(null)}
              onSuccess={fetchProfiles}
            />
          )}
        </div>
      );
    };

    export default ProfilesTab;